import { z } from 'zod'
import { MESSAGE_CHANNELS } from '../constants/messaging.js'
import { clientFilterSchema } from './clients.js'

export const channelSchema = z.enum(MESSAGE_CHANNELS)

/* -------------------------------------------------------------------------- */
/*  Templates                                                                  */
/* -------------------------------------------------------------------------- */

export const createTemplateSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120),
  channel: channelSchema,
  // Email needs a subject; SMS/WhatsApp don't have one. Enforced by the refine below.
  subject: z.string().trim().max(200).optional(),
  body: z.string().trim().min(1, 'Message body is required').max(2000),
})
export type CreateTemplateInput = z.infer<typeof createTemplateSchema>

export const updateTemplateSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    subject: z.string().trim().max(200).optional(),
    body: z.string().trim().min(1).max(2000).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })
export type UpdateTemplateInput = z.infer<typeof updateTemplateSchema>

/* -------------------------------------------------------------------------- */
/*  Campaigns                                                                   */
/* -------------------------------------------------------------------------- */

const bodySource = z
  .object({
    channel: channelSchema,
    templateId: z.string().optional(),
    subject: z.string().trim().max(200).optional(),
    body: z.string().trim().max(2000).optional(),
  })
  // Either reuse a template or write an ad-hoc body — but one of them must exist,
  // or there's nothing to send.
  .refine((v) => Boolean(v.templateId) || Boolean(v.body), {
    path: ['body'],
    message: 'Choose a template or write a message',
  })

export const campaignAudienceSchema = z.object({
  /** Reuses the exact Phase 2 filter engine — same audience as the client list. */
  filter: clientFilterSchema,
})

export const previewCampaignSchema = campaignAudienceSchema.extend({
  channel: channelSchema,
})
export type PreviewCampaignInput = z.infer<typeof previewCampaignSchema>

export const createCampaignSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(120),
    channel: channelSchema,
    templateId: z.string().optional(),
    subject: z.string().trim().max(200).optional(),
    body: z.string().trim().max(2000).optional(),
    filter: clientFilterSchema,
    /** ISO datetime to schedule for; omitted means send now. */
    scheduledAt: z
      .union([z.literal(''), z.string().datetime({ offset: true })])
      .optional()
      .transform((v) => (v === '' ? undefined : v)),
  })
  .refine((v) => Boolean(v.templateId) || Boolean(v.body), {
    path: ['body'],
    message: 'Choose a template or write a message',
  })
export type CreateCampaignInput = z.infer<typeof createCampaignSchema>

export { bodySource }

/* -------------------------------------------------------------------------- */
/*  Manual single send                                                         */
/* -------------------------------------------------------------------------- */

export const manualSendSchema = z
  .object({
    channel: channelSchema,
    templateId: z.string().optional(),
    subject: z.string().trim().max(200).optional(),
    body: z.string().trim().max(2000).optional(),
  })
  .refine((v) => Boolean(v.templateId) || Boolean(v.body), {
    path: ['body'],
    message: 'Choose a template or write a message',
  })
export type ManualSendInput = z.infer<typeof manualSendSchema>
